import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Send, X, Plus, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';

export const Notification = ({ productId, emailList }) => {
  const [emails, setEmails] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  useEffect(() => {
    setEmails(emailList)
  }, [emailList]);

  // Email validation for @effem.com domain
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@effem\.com$/;
    return emailRegex.test(email);
  };

  // Add email as tag
  const handleAddEmail = () => {
    const trimmedEmail = currentInput.trim();
    
    if (!trimmedEmail) {
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setError('Email must be a valid @effem.com address');
      return;
    }
    
    if (emails.includes(trimmedEmail)) {
      setError('Email already added');
      return;
    }
    
    setEmails([...emails, trimmedEmail]);
    setCurrentInput('');
    setError('');
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Remove email tag
  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  // Check if notify button should be enabled
  const isNotifyEnabled = () => {
    const hasValidEmail = emails.length > 0;
    const hasMessage = message.trim() !== '';
    return hasValidEmail && hasMessage;
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle notify button click
  const handleNotify = async () => {
    setError('');
    
    // Validate that we have at least one email
    if (emails.length === 0) {
      setError('Please add at least one email address');
      return;
    }

    // Validate message
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        productId: productId,
        emails: emails,
        message: message.trim()
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/notify`, payload);
      
      // Check response status
      if (response.data?.status_code === 200 && response.data?.response?.status === 'success') {
        setSnackbar({
          open: true,
          message: response.data?.message || 'Notification sent successfully!',
          severity: 'success'
        });
        
        // Reset message after successful submission, keep emails
        setMessage('');
      } else {
        setSnackbar({
          open: true,
          message: 'Notification sent but status is unclear',
          severity: 'warning'
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to send notification. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-3 space-y-4">
      <h3 className="text-[16px] font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <Mail className="h-5 text-[#FF03F3]" />
        Send Notification
      </h3>

      {/* Email Tags Input */}
      <div className="space-y-3">
        <label className="block text-[14px] font-medium text-gray-700">
          Email Recipients <span className="text-red-500">*</span>
        </label>
        
        {/* Email Tags Display */}
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {emails.map((email, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md text-sm"
              >
                <span>{email}</span>
                <button
                  onClick={() => removeEmail(email)}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  title="Remove email"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input field with Add button */}
        <div className="flex gap-2">
          <input
            type="email"
            value={currentInput}
            onChange={(e) => {
              setCurrentInput(e.target.value);
              setError('');
            }}
            onKeyPress={handleKeyPress}
            placeholder="Add Email and press Enter or click Add"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF03F3] focus:border-transparent outline-none transition-all h-[45px]"
          />
          <button
            onClick={handleAddEmail}
            className="px-6 py-2 bg-[#7dc1a9] text-white rounded-lg hover:bg-[#7dc1a9] transition-colors font-medium h-[45px] whitespace-nowrap"
          >
            Add
          </button>
        </div>

        {/* Verification Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> Please recheck and verify all email addresses before sending the notification.
          </p>
        </div>
      </div>

      {/* Message Text Area */}
        <div className="space-y-2">
          <label className="block text-[14px] font-medium text-gray-700">
            Message <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            {/* <MessageSquare className="absolute left-3 top-3 h-4 text-gray-400" /> */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your notification message..."
              rows={4}
              className="w-full pl-3 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF03F3] focus:border-transparent outline-none transition-all resize-none"
            />
          </div>
        </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Notify Button */}
        <div className="flex justify-end">
          <button
            onClick={handleNotify}
            disabled={!isNotifyEnabled() || loading}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all ${
              isNotifyEnabled() && !loading
                ? 'bg-[#7dc1a9] text-white hover:bg-[#7dc1a9] shadow-md hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Send className="h-4 w-4" />
            {loading ? 'Sending...' : 'Notify'}
          </button>
        </div>

      {/* Snackbar for success/error notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

